import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import UserAvatar from "@/components/UserAvatar";
import { apiFetch } from "@/api";
import {
  ArrowRight,
  CalendarCheck,
  ChevronDown,
  FileText,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  MonitorPlay,
  PenLine,
  RotateCcw,
  Sparkles,
  Target,
  Timer,
  Users,
  X,
} from "lucide-react";

/* Prepare mega-menu structure — single source for desktop dropdown + mobile drawer.
   Items with `soon` are announced but not released yet: visible, tagged, non-clickable. */
const PREPARE_GROUPS = [
  {
    title: "PRACTICE & LEARN",
    testId: "prepare-group-practice-learn",
    items: [
      { title: "Custom Practice", desc: "Tailored questions by chapter & difficulty", href: "/practice-setup", icon: PenLine, testId: "prepare-item-custom-practice" },
      { title: "Smart Practice", desc: "Adaptive AI practice targeted at weak areas", href: "/smart-practice", icon: Target, testId: "prepare-item-smart-practice" },
      { title: "Reattempt", desc: "Review & re-fix missed questions", href: "/reattempt", icon: RotateCcw, testId: "prepare-item-reattempt" },
      { title: "Exam Mode", desc: "Timed full-length mock examinations", href: "/exam-mode", icon: Timer, testId: "prepare-item-exam-mode" },
      { title: "Watch & Learn", desc: "Curated video lessons & step-by-step guides", href: "/learn/videos", icon: MonitorPlay, testId: "prepare-item-watch-learn" },
      { title: "PYQs", desc: "Real CBSE & ICSE past papers — view & download", href: "/pyq", icon: FileText, testId: "prepare-item-pyqs" },
    ],
  },
  {
    title: "AI & PLANNING",
    testId: "prepare-group-ai-planning",
    items: [
      { title: "Ask Abhyas AI", desc: "Instant academic doubt solver & concept explainer", href: "/abhyas-ai", icon: Sparkles, testId: "prepare-item-ask-ai" },
      { title: "Study Planner", desc: "Personalized study timetable & revision tracker", href: "/planner", icon: CalendarCheck, testId: "prepare-item-study-planner" },
    ],
  },
];

const SoonBadge = () => (
  <span className="ml-2 inline-block rounded border border-[#FACC15]/40 bg-[#FEF9C3] px-1.5 py-0.5 align-middle text-[10px] font-bold text-[#14532D]">
    Soon
  </span>
);

const PrepareItem = ({ item, onNavigate, dense = false }) => {
  const Icon = item.icon;
  const inner = (
    <>
      <span
        className={`flex shrink-0 items-center justify-center rounded-lg border ${
          item.soon
            ? "border-[#E2E8F0] bg-[#F8FAF7] text-[#647067]"
            : "border-[#16A34A]/15 bg-[#16A34A]/8 text-[#16A34A]"
        } ${dense ? "h-8 w-8" : "h-9 w-9"}`}
      >
        <Icon className={dense ? "h-4 w-4" : "h-4.5 w-4.5"} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-[#17201A]">
          {item.title}
          {item.soon && <SoonBadge />}
        </span>
        {!dense && (
          <span className="mt-0.5 block text-xs font-medium leading-snug text-[#647067]">
            {item.desc}
          </span>
        )}
      </span>
    </>
  );
  const base = `flex items-start gap-3 rounded-xl ${dense ? "p-2.5" : "p-2.5"}`;
  if (item.soon) {
    return (
      <div
        data-testid={item.testId}
        aria-disabled="true"
        className={`${base} cursor-not-allowed select-none opacity-50`}
      >
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={item.href}
      data-testid={item.testId}
      onClick={onNavigate}
      className={`${base} transition-colors duration-200 hover:bg-[#F8FAF7]`}
    >
      {inner}
    </Link>
  );
};

const SiteHeader = ({ loggedIn }) => {
  const [prepareOpen, setPrepareOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPrepareOpen, setDrawerPrepareOpen] = useState(false);
  const [me, setMe] = useState(null);
  const prepareRef = useRef(null);

  // Fetch account details lazily — only when the drawer opens for a logged-in user
  useEffect(() => {
    if (!drawerOpen || !loggedIn || me) return;
    apiFetch("/me").then(setMe).catch(() => {});
  }, [drawerOpen, loggedIn, me]);

  const signOut = (dest) => {
    apiFetch("/auth/logout", { method: "POST" }).catch(() => {});
    localStorage.removeItem("abhyas_token");
    localStorage.removeItem("abhyas_profile");
    window.location.href = dest;
  };

  const planLine = () => {
    if (!me) return null;
    if (me.planStatus === "active" && me.plan)
      return `${me.plan} plan · Renews ${new Date(me.planRenewsAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`;
    if (me.weekPassActive) return "7-Day Pass · active";
    return "Free plan";
  };

  // Close the desktop mega-menu on outside click / Escape
  useEffect(() => {
    if (!prepareOpen) return;
    const onDown = (e) => {
      if (prepareRef.current && !prepareRef.current.contains(e.target))
        setPrepareOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setPrepareOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [prepareOpen]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const navLinkClass =
    "relative whitespace-nowrap text-[15px] font-semibold text-[#17201A] transition-colors duration-200 after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-full after:origin-center after:scale-x-0 after:rounded-full after:bg-[#16A34A] after:transition-transform after:duration-300 hover:text-[#16A34A] hover:after:scale-x-100";

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerPrepareOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-[#14532D]/10 bg-[#F8FAF7]/90 backdrop-blur-md transition-all duration-200"
    >
      <div className="mx-auto flex w-[94%] max-w-[1520px] items-center justify-between py-3.5 lg:px-2">
        <Link to="/" data-testid="header-logo" className="flex shrink-0 items-center gap-3">
          <img src="/logo.png" alt="Abhyas" className="h-10 w-auto" />
        </Link>

        {/* Center nav — desktop & tablet */}
        <nav className="hidden items-center gap-8 md:flex">
          <a href="#top" data-testid="nav-link-home" className={navLinkClass}>
            Home
          </a>
          <div
            ref={prepareRef}
            className="relative"
            onMouseEnter={() => setPrepareOpen(true)}
            onMouseLeave={() => setPrepareOpen(false)}
          >
            <button
              type="button"
              data-testid="nav-link-prepare"
              aria-expanded={prepareOpen}
              aria-haspopup="true"
              onClick={() => setPrepareOpen((o) => !o)}
              className={`${navLinkClass} flex items-center gap-1 ${
                prepareOpen ? "text-[#16A34A]" : ""
              }`}
            >
              Prepare
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${
                  prepareOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {/* Mega-menu */}
            <div
              data-testid="prepare-dropdown"
              className={`absolute left-1/2 top-full z-50 mt-2 w-[min(540px,90vw)] -translate-x-1/2 rounded-2xl border border-[#14532D]/12 bg-white p-6 shadow-[0_20px_40px_-15px_rgba(20,83,45,0.12)] transition-all duration-200 ease-out ${
                prepareOpen
                  ? "translate-y-0 opacity-100"
                  : "pointer-events-none translate-y-1 opacity-0"
              }`}
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {PREPARE_GROUPS.map((group) => (
                  <div key={group.title} data-testid={group.testId}>
                    <p className="mb-2 px-2.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#647067]">
                      {group.title}
                    </p>
                    <div className="space-y-0.5">
                      {group.items.map((item) => (
                        <PrepareItem
                          key={item.title}
                          item={item}
                          onNavigate={() => setPrepareOpen(false)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <a href="#pricing" data-testid="nav-link-pricing" className={navLinkClass}>
            Pricing
          </a>
        </nav>

        {/* Right side — desktop & tablet */}
        <div className="hidden shrink-0 items-center gap-3 md:flex">
          {loggedIn ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-button-continue"
                className="flex items-center gap-1.5 rounded-full bg-[#16A34A] px-5 py-2 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-[#14532D]"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Link>
              {/* Circular avatar opens the account dropdown (profile, plan, sign out) */}
              <UserAvatar />
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-button-login"
                className="px-4 py-2 text-sm font-semibold text-[#17201A] transition-colors duration-200 hover:text-[#16A34A]"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                data-testid="nav-button-signup"
                data-magnetic="true"
                className="rounded-full bg-[#16A34A] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-[#14532D]"
              >
                Sign Up
              </Link>
            </>
          )}
        </div>

        {/* Right side — mobile (no bottom nav; Continue visible; profile lives inside the drawer) */}
        <div className="flex items-center gap-2 md:hidden">
          {loggedIn ? (
            <Link
              to="/dashboard"
              data-testid="mobile-nav-button-continue"
              className="flex items-center gap-1 rounded-full bg-[#16A34A] px-3.5 py-2 text-xs font-bold text-white shadow-sm transition-colors duration-200 hover:bg-[#14532D]"
            >
              Continue
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="mobile-nav-button-login"
                className="px-2.5 py-2 text-sm font-semibold text-[#17201A] transition-colors duration-200 hover:text-[#16A34A]"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                data-testid="mobile-nav-button-signup"
                className="rounded-full bg-[#16A34A] px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors duration-200 hover:bg-[#14532D]"
              >
                Sign Up
              </Link>
            </>
          )}
          <button
            type="button"
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
            className="rounded-xl border border-[#E2E8F0] bg-white p-2.5 text-[#17201A]"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile side drawer — PORTALED to document.body: the sticky header's
          backdrop-blur would otherwise become the containing block for these
          fixed layers and squash them into the header strip behind the hero. */}
      {drawerOpen &&
        createPortal(
          <div className="md:hidden">
            <div
              data-testid="mobile-menu-backdrop"
              onClick={closeDrawer}
              className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-sm"
            />
            <div
              data-testid="mobile-menu"
              className="drawer-in-right fixed bottom-0 right-0 top-0 z-[80] flex w-[85%] max-w-[340px] flex-col overflow-y-auto rounded-l-[1.75rem] border-l border-[#E3ECE6] bg-white p-6 shadow-2xl"
            >
            <div className="flex items-center justify-between">
              <img src="/logo.png" alt="Abhyas" className="h-9 w-auto" />
              <button
                type="button"
                data-testid="mobile-menu-close-button"
                aria-label="Close menu"
                onClick={closeDrawer}
                className="rounded-xl border border-[#E2E8F0] bg-[#F8FAF7] p-2.5 text-[#17201A] transition-colors duration-200 hover:text-[#16A34A]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-8 flex flex-col gap-1">
              <a
                href="#top"
                data-testid="mobile-nav-home"
                onClick={closeDrawer}
                className="rounded-xl px-4 py-3.5 text-base font-bold text-[#17201A] transition-colors duration-200 hover:bg-[#F8FAF7] hover:text-[#16A34A]"
              >
                Home
              </a>
              <button
                type="button"
                data-testid="mobile-nav-prepare-toggle"
                aria-expanded={drawerPrepareOpen}
                onClick={() => setDrawerPrepareOpen((o) => !o)}
                className="flex items-center justify-between rounded-xl px-4 py-3.5 text-left text-base font-bold text-[#17201A] transition-colors duration-200 hover:bg-[#F8FAF7] hover:text-[#16A34A]"
              >
                Prepare
                <ChevronDown
                  className={`h-5 w-5 text-[#647067] transition-transform duration-300 ${
                    drawerPrepareOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                data-testid="mobile-prepare-panel"
                className={`grid transition-all duration-300 ease-out ${
                  drawerPrepareOpen
                    ? "grid-rows-[1fr] opacity-100"
                    : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="ml-2 space-y-4 border-l-2 border-[#16A34A]/15 py-2 pl-3">
                    {PREPARE_GROUPS.map((group) => (
                      <div key={group.title} data-testid={`mobile-${group.testId}`}>
                        <p className="mb-1 px-2.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#647067]">
                          {group.title}
                        </p>
                        <div className="space-y-0.5">
                          {group.items.map((item) => (
                            <PrepareItem
                              key={item.title}
                              item={item}
                              dense
                              onNavigate={closeDrawer}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <a
                href="#pricing"
                data-testid="mobile-nav-pricing"
                onClick={closeDrawer}
                className="rounded-xl px-4 py-3.5 text-base font-bold text-[#17201A] transition-colors duration-200 hover:bg-[#F8FAF7] hover:text-[#16A34A]"
              >
                Pricing
              </a>
            </nav>

            {/* Account — profile/avatar lives HERE on mobile, not in the header */}
            {loggedIn && (
              <div data-testid="mobile-account-section" className="mt-6 border-t border-[#E3ECE6] pt-5">
                <div className="flex items-center gap-3 px-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#16A34A] font-display text-sm font-extrabold text-white">
                    {(me?.name || "S").trim()[0].toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p data-testid="mobile-account-name" className="truncate font-display text-sm font-extrabold">
                      {me?.name || "Account"}
                    </p>
                    {planLine() && (
                      <p data-testid="mobile-account-plan" className="truncate text-xs font-bold text-[#16A34A]">
                        {planLine()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-col gap-0.5">
                  {[
                    { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard, testId: "mobile-account-dashboard" },
                    { label: "Practice History", to: "/progress", icon: History, testId: "mobile-account-history" },
                    { label: "Ask Abhyas AI", to: "/abhyas-ai", icon: Sparkles, testId: "mobile-account-ai" },
                  ].map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={l.testId}
                      onClick={closeDrawer}
                      className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold text-[#17201A] transition-colors duration-200 hover:bg-[#F8FAF7] hover:text-[#16A34A]"
                    >
                      <l.icon className="h-4 w-4 text-[#16A34A]" />
                      {l.label}
                    </Link>
                  ))}
                  <button
                    type="button"
                    data-testid="mobile-account-switch"
                    onClick={() => signOut("/login")}
                    className="flex items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold text-[#17201A] transition-colors duration-200 hover:bg-[#F8FAF7] hover:text-[#16A34A]"
                  >
                    <Users className="h-4 w-4 text-[#16A34A]" />
                    Switch Account
                  </button>
                  <button
                    type="button"
                    data-testid="mobile-account-sign-out"
                    onClick={() => signOut("/")}
                    className="flex items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold text-red-600 transition-colors duration-200 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
            <p className="mt-auto pt-8 text-center text-xs font-semibold text-[#647067]">
              Abhyas — practice smarter.
            </p>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
};

export default SiteHeader;
